import {
  createContext,
  Dispatch,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useToast } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { Sse } from '../helpers';
import {
  useDispatchers,
  Dispatchers,
  ActionRequest,
  initialStateOperationStarted,
  initialStateSelfieCaptured,
  initialStateOperationFinished,
  initialStateOperationExpired,
  StateRequest,
  DispatchersTitles,
} from '../reducers';
import {
  SseEvents,
  SseStatus,
  SseResultObject,
  OperationStatus,
  DEMOS_BE,
} from '../states';
import { capitalize } from '../utils';
import { useAuth } from './AuthProvider';

type IProps = {
  children: React.ReactNode;
  url: string;
};

interface CustomEvent extends MessageEvent {
  type: keyof typeof SseEvents;
}

type ContextProps = {
  url: string;
  error: boolean;
  connection: EventSource | undefined;
  status?: SseStatus;
  connect(operationId: string): void;
  disconnect(): void;
  resetStates(): void;
  setIsOperationFinished: Dispatch<boolean>;
  operationStartedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.OPERATION_STARTED];
    status?: OperationStatus;
  };
  selfieCapturedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.SELFIE_CAPTURED];
    status?: OperationStatus;
  };
  operationFinishedState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.OPERATION_FINISHED];
    status?: OperationStatus;
  };
  operationExpiredState: {
    loading: boolean;
    error: boolean;
    data?: SseResultObject[SseEvents.OPERATION_EXPIRED];
    status?: OperationStatus;
  };
};

const Ctx = createContext<ContextProps>({
  url: '',
  error: false,
  connection: undefined,
  connect: () => {},
  disconnect: () => {},
  resetStates: () => {},
  setIsOperationFinished: () => {},
  operationStartedState: initialStateOperationStarted,
  selfieCapturedState: initialStateSelfieCaptured,
  operationFinishedState: initialStateOperationFinished,
  operationExpiredState: initialStateOperationExpired,
});

export const DataProvider = ({ children, url }: IProps) => {
  const { dispatchers, cardStates } = useDispatchers();
  const [connection, setConnection] = useState<EventSource>();
  const [error, setError] = useState<boolean>(false);
  const [isOperationFinished, setIsOperationFinished] =
    useState<boolean>(false);
  const [status, setStatus] = useState<SseStatus>(SseStatus.connecting);
  const { t } = useTranslation();
  const { toastManager } = useToast();
  const { token } = useAuth();
  const isDemosBe = url?.includes(DEMOS_BE);

  const {
    operationStarted,
    selfieCaptured,
    operationFinished,
    operationExpired,
  } = cardStates;

  const toastError = () => {
    toastManager({
      type: 'error',
      message: t('There was a server error'),
      duration: 2000,
    });
    setError(true);
  };

  const checkStatus = <T,>(
    item: StateRequest<T>,
    checkSuccess: () => boolean,
    checkError?: () => boolean
  ): OperationStatus => {
    return (!item.data && !item.error) || item.loading
      ? OperationStatus.STARTED
      : item.error || (checkError && checkError())
      ? OperationStatus.CONNECTION_ERROR
      : checkSuccess()
      ? OperationStatus.SUCCEEDED
      : OperationStatus.DENIED;
  };

  const dispatchResults = (event: CustomEvent) => {
    dispatchers[`dispatch${DispatchersTitles[event.type]}`]({
      type: ActionRequest.SET_DATA,
      payload: JSON.parse(event.data),
    });
  };

  const setStatesError = () => {
    Object.keys(cardStates).forEach((state: string) => {
      if (!cardStates[state as keyof typeof cardStates].data) {
        const capitalizedState = capitalize(state);
        const dispatcherKey =
          `dispatch${capitalizedState}` as keyof Dispatchers;

        if (dispatchers[dispatcherKey])
          dispatchers[dispatcherKey]({
            type: ActionRequest.SET_ERROR,
          });
      }
    });
  };

  const resetStates = () => {
    Object.keys(cardStates).forEach((state: string) => {
      const capitalizedState = capitalize(state);
      const dispatcherKey = `dispatch${capitalizedState}` as keyof Dispatchers;

      if (dispatchers[dispatcherKey])
        dispatchers[dispatcherKey]({
          type: ActionRequest.START_REQUEST,
        });
    });
  };

  const connect = (operationId: string) => {
    setError(false);
    if (operationId && token) {
      const source = Sse({
        url: `${url}/sse${isDemosBe ? `/${operationId}?token=${token}` : ''}`,
        setStatus,
        toastError,
        setIsOperationFinished,
        setError,
        dispatchResults,
      });
      setConnection(source);
      setIsOperationFinished(false);
    } else {
      toastError();
    }
  };

  const disconnect = () => connection?.close();

  useEffect(() => {
    if (status === SseStatus.error || isOperationFinished) {
      setStatesError();
    }
  }, [status, isOperationFinished]);

  return (
    <Ctx.Provider
      value={{
        url,
        connection,
        error,
        status,
        connect,
        disconnect,
        resetStates,
        setIsOperationFinished,
        operationStartedState: operationStarted,
        selfieCapturedState: selfieCaptured,
        operationFinishedState: {
          ...operationFinished,
          status: checkStatus(
            operationFinished,
            () => operationFinished.data?.status === OperationStatus.SUCCEEDED,
            () => operationFinished.data?.status === OperationStatus.ERROR
          ),
        },
        operationExpiredState: operationExpired,
      }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useData = () => useContext(Ctx);
